/**
 * Global Cache Manager
 *
 * This module ENABLES ALL caching mechanisms across the application:
 * - AsyncStorage (mobile persistent storage)
 * - localStorage (web persistent storage)
 * - sessionStorage (web session storage)
 *
 * All values are now cached without any restrictions.
 * Previously this file disabled caching with a whitelist, but now ALL caching is enabled.
 */

import AsyncStorage from "@react-native-async-storage/async-storage";

// Store original methods
const originalAsyncStorageSetItem = AsyncStorage.setItem.bind(AsyncStorage);
const originalAsyncStorageGetItem = AsyncStorage.getItem.bind(AsyncStorage);

// Whitelist for critical workflow data (required for app to function properly)
const OAUTH_WHITELIST = [
  // Authentication & OAuth
  "oauthRedirectUri", // OAuth redirect for YouTube/Twitch
  "@loginData", // User login state
  "username", // Current username
  "profilePicture", // User profile picture
  "userID", // User ID
  "@locationDetails", // User location details

  // Event creation/management workflow
  "createEventData", // Multi-step event creation

  // Event sharing & signup flow
  "pendingEventUrl", // Pending event URL for unauthenticated users
  "eventShareAction", // Event share action tracking
  "gameShowAction", // GameShow action tracking
  "gameShowActionTimestamp", // GameShow action timestamp
  "gameShowRedirectURL", // GameShow redirect URL
  "gameShowURLTimestamp", // GameShow URL timestamp
  "signupFlowContext", // Unified signup flow context
  "loginFlowContext", // Unified login flow context
  "showWelcomeAfterSignup", // Welcome modal trigger
  "postConfirmationRedirect", // Post-signup redirect URL
  "postLoginRedirect", // Post-login redirect URL

  // Stream settings & broadcaster
  "streamSettings", // User stream configuration
  "isKOBroadcasterDownloaded", // Broadcaster download status
  "koBroadcasterDownloadTime", // Broadcaster download timestamp
  "twitchConnectedModalShown", // Modal shown status

  // Notification caching
  "cachedNotifications", // Cached notifications for popup
  "hasFetchedNotifications", // Flag to track if notifications have been fetched
  "challengeNotificationCount", // Challenge notification count
  "chatNotificationCount", // Chat notification count

  // Expo & System
  "EXPO_CONSTANTS_INSTALLATION_ID", // Expo installation identifier
];

/**
 * CACHING ENABLED - All AsyncStorage operations are now allowed
 * (Previously blocked non-whitelisted keys, now allowing everything)
 */
AsyncStorage.setItem = async (key: string, value: string): Promise<void> => {
  return originalAsyncStorageSetItem(key, value);
};

/**
 * CACHING ENABLED - All AsyncStorage operations are now allowed
 */
AsyncStorage.getItem = async (key: string): Promise<string | null> => {
  return originalAsyncStorageGetItem(key);
};

/**
 * CACHING ENABLED - All AsyncStorage operations are now allowed
 */
AsyncStorage.multiSet = async (
  keyValuePairs: [string, string][]
): Promise<void> => {
  return originalAsyncStorageSetItem.constructor.prototype.multiSet.call(
    AsyncStorage,
    keyValuePairs
  );
};

/**
 * CACHING ENABLED - All AsyncStorage operations are now allowed
 */
AsyncStorage.multiGet = async (
  keys: string[]
): Promise<readonly [string, string | null][]> => {
  return originalAsyncStorageGetItem.constructor.prototype.multiGet.call(
    AsyncStorage,
    keys
  );
};

/**
 * CACHING ENABLED - localStorage and sessionStorage for web
 */
if (typeof window !== "undefined") {
  // Store originals
  const originalLocalStorageSetItem = window.localStorage.setItem.bind(
    window.localStorage
  );
  const originalLocalStorageGetItem = window.localStorage.getItem.bind(
    window.localStorage
  );
  const originalSessionStorageSetItem = window.sessionStorage.setItem.bind(
    window.sessionStorage
  );
  const originalSessionStorageGetItem = window.sessionStorage.getItem.bind(
    window.sessionStorage
  );

  // Override localStorage.setItem - ALLOW ALL
  window.localStorage.setItem = (key: string, value: string): void => {
    return originalLocalStorageSetItem(key, value);
  };

  // Override localStorage.getItem - ALLOW ALL
  window.localStorage.getItem = (key: string): string | null => {
    return originalLocalStorageGetItem(key);
  };

  // Override sessionStorage.setItem - ALLOW ALL
  window.sessionStorage.setItem = (key: string, value: string): void => {
    return originalSessionStorageSetItem(key, value);
  };

  // Override sessionStorage.getItem - ALLOW ALL
  window.sessionStorage.getItem = (key: string): string | null => {
    return originalSessionStorageGetItem(key);
  };

}

export default {};
